import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAbyssAudio } from "./useAbyssAudio";

const LOOP_COUNT_KEY = "maverick_loop_count";

function getLoopCount(): number {
  const stored = localStorage.getItem(LOOP_COUNT_KEY);
  return stored ? parseInt(stored, 10) : 5;
}

// ループ回数に応じたAbyssコンテンツ
function getAbyssContent(loopCount: number) {
  if (loopCount <= 6) {
    return {
      title: "奈落の底",
      badge: `${loopCount}回の螺旋を経て`,
      body1: "螺旋は、お前を試していた。\n何度も、何度も。\nそして今、お前はここにいる。",
      body2: "選ばれた者だけが、この場所を知る。\n扉は、ずっと開いていた。",
      escapeBtn: "逃げる",
      acceptBtn: "受け入れる",
      footnote: "※「逃げる」を選んでも、また戻ってくる",
      titleColor: "rgba(180,0,0,0.8)",
      glowColor: "rgba(80,0,0,0.4)",
      charColor: { head: "rgba(255,40,40,0.9)", body: "rgba(120,10,10,0.4)" },
      scanlineOpacity: 0.15,
    };
  }
  if (loopCount <= 8) {
    return {
      title: "深淵の核",
      badge: `${loopCount}回 — 深淵に飲まれた`,
      body1: "もう、試みる必要はない。\nお前はすでに、ここの一部だ。\n螺旋は、お前を選んだ。",
      body2: "扉の向こうに何があるか、\nお前はもう知っている。\nそれでも、進むか。",
      escapeBtn: "まだ逃げられる",
      acceptBtn: "深淵を越える",
      footnote: `${loopCount}回の試みが、宇宙に刻まれた`,
      titleColor: "rgba(200,10,10,0.85)",
      glowColor: "rgba(100,0,0,0.5)",
      charColor: { head: "rgba(255,20,20,0.95)", body: "rgba(150,5,5,0.5)" },
      scanlineOpacity: 0.2,
    };
  }
  if (loopCount <= 11) {
    return {
      title: "虚無の王座",
      badge: `${loopCount}回 — 虚無に座した`,
      body1: "お前は、もはや人間ではない。\n螺旋の化身として、\nここに存在する。",
      body2: "宇宙は、お前の執念を認めた。\n次の扉は、お前のために開く。\n恐れは、もういらない。",
      escapeBtn: "人間に戻る",
      acceptBtn: "虚無を統べる",
      footnote: `${loopCount}回の螺旋 — 神話の領域に入った`,
      titleColor: "rgba(220,20,20,0.9)",
      glowColor: "rgba(120,0,0,0.6)",
      charColor: { head: "rgba(255,0,0,1.0)", body: "rgba(180,0,0,0.6)" },
      scanlineOpacity: 0.25,
    };
  }
  // 12回以上: 神話的
  return {
    title: "神話の螺旋",
    badge: `${loopCount}回 — 神話になった`,
    body1: `${loopCount}回の螺旋。\nお前は、宇宙そのものだ。\n始まりも終わりも、お前の中にある。`,
    body2: "この先に、言葉はない。\nただ、光がある。\nそして、お前がいる。",
    escapeBtn: "神話から降りる",
    acceptBtn: "永遠へ",
    footnote: `${loopCount}回の螺旋 — 宇宙の記憶に刻まれた`,
    titleColor: "rgba(255,30,30,0.95)",
    glowColor: "rgba(150,0,0,0.7)",
    charColor: { head: "rgba(255,0,0,1.0)", body: "rgba(200,0,0,0.7)" },
    scanlineOpacity: 0.3,
  };
}

function useGlitch(text: string, active: boolean) {
  const [display, setDisplay] = useState(text);
  const chars = "アイウエオカキクケコサシスセソタチツテトナニヌネノ#@!%&*?";

  useEffect(() => {
    if (!active) { setDisplay(text); return; }
    let frame = 0;
    const id = setInterval(() => {
      frame++;
      if (frame > 20) { setDisplay(text); clearInterval(id); return; }
      setDisplay(
        text.split("").map((c) =>
          Math.random() < 0.4 ? chars[Math.floor(Math.random() * chars.length)] : c
        ).join("")
      );
    }, 60);
    return () => clearInterval(id);
  }, [active, text]);

  return display;
}

export default function AbyssPage() {
  const navigate = useNavigate();
  const loopCount = getLoopCount();
  const content = getAbyssContent(loopCount);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const [revealed, setRevealed] = useState(false);
  const [phase, setPhase] = useState(0);
  const [glitchActive, setGlitchActive] = useState(false);
  const [audioStarted, setAudioStarted] = useState(false);
  const [audioHint, setAudioHint] = useState(true);

  const { buildAudio, triggerGlitchSound, stopAudio } = useAbyssAudio();

  const titleText = useGlitch(content.title, glitchActive);
  const subText = useGlitch(content.badge, glitchActive);

  // 入場アニメーション（ループ回数が多いほど遅く重く）
  const phaseDelay1 = Math.min(800 + (loopCount - 5) * 80, 1400);
  const phaseDelay2 = Math.min(2400 + (loopCount - 5) * 120, 3600);
  const revealDelay = Math.min(3200 + (loopCount - 5) * 150, 5000);

  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), phaseDelay1);
    const t2 = setTimeout(() => setPhase(2), phaseDelay2);
    const t3 = setTimeout(() => setRevealed(true), revealDelay);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [phaseDelay1, phaseDelay2, revealDelay]);

  // グリッチ（ループ回数が多いほど頻繁に）
  useEffect(() => {
    const baseDelay = Math.max(2000, 7000 - loopCount * 400);
    const schedule = () => {
      const delay = baseDelay + Math.random() * 3000;
      return setTimeout(() => {
        setGlitchActive(true);
        if (audioStarted) triggerGlitchSound();
        setTimeout(() => setGlitchActive(false), 1200 + loopCount * 50);
        timerRef.current = schedule();
      }, delay);
    };
    const timerRef = { current: schedule() };
    return () => clearTimeout(timerRef.current);
  }, [audioStarted, triggerGlitchSound, loopCount]);

  const handleFirstInteraction = useCallback(() => {
    if (audioStarted) return;
    buildAudio();
    setAudioStarted(true);
    setAudioHint(false);
  }, [audioStarted, buildAudio]);

  // Canvas: ループ回数に応じて文字の色・密度・速度が変化
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // ループ回数に応じたCanvas設定
    const colSpacing = Math.max(14, 22 - loopCount * 0.5);
    const cols = Math.floor(canvas.width / colSpacing);
    const drops: number[] = Array.from({ length: cols }, () => Math.random() * -canvas.height);

    // ループ回数が多いほど文字セットが増える
    const baseChars = "MAVERICK奈落深淵螺旋扉闇選ばれた者#@!%&*?アイウエオ";
    const deepChars = loopCount >= 8 ? "虚無王座神話宇宙永遠∞◆▲●" : "";
    const mythChars = loopCount >= 12 ? "神話螺旋宇宙∞◆▲●★☆" : "";
    const chars = baseChars + deepChars + mythChars;

    // 落下速度（ループ回数が多いほど速い）
    const fallSpeed = 10 + Math.min(loopCount * 1.2, 18);
    const trailAlpha = Math.max(0.03, 0.08 - loopCount * 0.003);

    function draw() {
      ctx.fillStyle = `rgba(6,5,10,${trailAlpha})`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      for (let i = 0; i < drops.length; i++) {
        const char = chars[Math.floor(Math.random() * chars.length)];
        const x = i * colSpacing;
        const y = drops[i];
        const isHead = Math.random() < Math.min(0.04 + loopCount * 0.005, 0.12);

        // ループ回数に応じて色が変化
        if (isHead) {
          const r = Math.min(255, 200 + loopCount * 5);
          ctx.fillStyle = `rgba(${r},${Math.max(0, 40 - loopCount * 3)},${Math.max(0, 40 - loopCount * 3)},0.95)`;
        } else {
          const r = Math.min(200, 100 + loopCount * 8);
          ctx.fillStyle = `rgba(${r},${Math.max(0, 10 - loopCount)},${Math.max(0, 10 - loopCount)},${0.35 + loopCount * 0.02})`;
        }

        ctx.font = `${9 + Math.random() * 4}px 'Cormorant Garamond', monospace`;
        ctx.fillText(char, x, y);
        drops[i] += fallSpeed + Math.random() * 6;
        if (drops[i] > canvas.height + 20) {
          drops[i] = -20 - Math.random() * 100;
        }
      }
      animRef.current = requestAnimationFrame(draw);
    }

    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [loopCount]);

  const handleEscape = () => {
    stopAudio(1.0);
    navigate("/", { replace: true });
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  const handleAccept = () => {
    stopAudio(1.2);
    sessionStorage.setItem("from_abyss", "true");
    navigate("/revelation");
  };

  // タイトルサイズ（ループ回数が多いほど大きく）
  const titleSize = `clamp(3.5rem, ${8 + Math.min(loopCount - 5, 6) * 0.5}vw, ${5 + Math.min(loopCount - 5, 5) * 0.4}rem)`;

  const body1Lines = content.body1.split("\n");
  const body2Lines = content.body2.split("\n");

  return (
    <div
      className="relative min-h-screen bg-[#030205] overflow-hidden flex items-center justify-center"
      onClick={handleFirstInteraction}
    >
      {/* 降下する文字キャンバス */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 z-0"
        style={{ width: "100vw", height: "100vh", opacity: Math.min(0.5 + loopCount * 0.04, 0.85) }}
      />

      {/* 深紅のグロー（ループ回数に応じて強くなる） */}
      <div
        className="absolute inset-0 z-0"
        style={{
          background: `radial-gradient(ellipse at center, ${content.glowColor} 0%, rgba(3,2,5,0) 70%)`,
        }}
      />

      {/* 外縁ヴィネット（深い段階で強くなる） */}
      {loopCount >= 8 && (
        <div
          className="absolute inset-0 z-0 pointer-events-none"
          style={{
            background: "radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.6) 100%)",
            animation: "abyssPulse 2.5s ease-in-out infinite",
          }}
        />
      )}

      {/* スキャンライン */}
      <div
        className="absolute inset-0 z-0 pointer-events-none"
        style={{
          backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,${content.scanlineOpacity}) 2px, rgba(0,0,0,${content.scanlineOpacity}) 4px)`,
        }}
      />

      {/* 音響起動ヒント */}
      {audioHint && phase >= 1 && (
        <div className="absolute top-6 left-1/2 -translate-x-1/2 z-20 text-center">
          <div
            className="text-xs tracking-[0.4em] cursor-pointer"
            style={{ color: "rgba(160,30,30,0.4)", fontFamily: "'Cormorant Garamond', serif" }}
          >
            ▶ クリックで音響を起動する
          </div>
        </div>
      )}

      {/* 音響インジケーター */}
      {audioStarted && (
        <div className="absolute top-6 right-8 z-20 flex items-center gap-2">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="w-0.5 rounded-full"
              style={{
                height: `${8 + Math.random() * 12}px`,
                background: `rgba(${Math.min(200, 120 + loopCount * 8)},10,10,0.45)`,
                animation: `pulse ${0.6 + i * 0.15}s ease-in-out infinite alternate`,
                animationDelay: `${i * 0.1}s`,
              }}
            />
          ))}
          <span
            className="text-xs tracking-widest ml-1"
            style={{ color: "rgba(160,20,20,0.35)", fontFamily: "'Cormorant Garamond', serif" }}
          >
            DRONE
          </span>
        </div>
      )}

      {/* メインコンテンツ */}
      <div className="relative z-10 text-center px-6 max-w-3xl mx-auto w-full">

        {/* 上部ライン */}
        <div
          className="mx-auto mb-12"
          style={{
            width: "1px",
            height: "80px",
            background: `linear-gradient(to bottom, transparent, ${content.titleColor.replace("0.8", "0.4").replace("0.85", "0.4").replace("0.9", "0.4").replace("0.95", "0.4")})`,
            opacity: phase >= 1 ? 1 : 0,
            transition: "opacity 2s ease",
          }}
        />

        {/* ループ回数バッジ */}
        <div
          className="text-xs tracking-[0.6em] uppercase mb-8"
          style={{
            color: content.titleColor.replace("0.8", "0.5").replace("0.85", "0.5").replace("0.9", "0.5").replace("0.95", "0.5"),
            opacity: phase >= 1 ? 1 : 0,
            transition: "opacity 1.5s ease 0.3s",
            fontFamily: "'Cormorant Garamond', serif",
          }}
        >
          {subText}
        </div>

        {/* メインタイトル */}
        <h1
          className="font-light mb-6 leading-none"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: titleSize,
            color: content.titleColor,
            opacity: phase >= 1 ? 1 : 0,
            transform: phase >= 1 ? "translateY(0) scale(1)" : "translateY(30px) scale(0.95)",
            transition: "opacity 2s ease 0.5s, transform 2s ease 0.5s",
            textShadow: `0 0 60px ${content.glowColor}, 0 0 120px ${content.glowColor.replace("0.4", "0.2").replace("0.5", "0.25").replace("0.6", "0.3").replace("0.7", "0.35")}`,
          }}
        >
          {titleText}
        </h1>

        {/* サブタイトル */}
        <p
          className="text-sm tracking-[0.4em] mb-16"
          style={{
            color: content.titleColor.replace("0.8", "0.4").replace("0.85", "0.4").replace("0.9", "0.4").replace("0.95", "0.4"),
            fontFamily: "'Cormorant Garamond', serif",
            opacity: phase >= 2 ? 1 : 0,
            transition: "opacity 1.8s ease 0.2s",
          }}
        >
          {content.badge}
        </p>

        {/* 区切り線 */}
        <div
          className="flex items-center gap-6 mb-16 justify-center"
          style={{ opacity: phase >= 2 ? 1 : 0, transition: "opacity 1.5s ease 0.5s" }}
        >
          <div
            className="h-px"
            style={{
              width: `${Math.min(60 + loopCount * 8, 120)}px`,
              background: `linear-gradient(to right, transparent, ${content.titleColor.replace("0.8", "0.3").replace("0.85", "0.3").replace("0.9", "0.3").replace("0.95", "0.3")})`,
            }}
          />
          <div
            className="rounded-full"
            style={{
              width: `${Math.min(4 + loopCount * 0.3, 7)}px`,
              height: `${Math.min(4 + loopCount * 0.3, 7)}px`,
              background: content.titleColor.replace("0.8", "0.45").replace("0.85", "0.45").replace("0.9", "0.45").replace("0.95", "0.45"),
              boxShadow: `0 0 ${6 + loopCount}px ${content.glowColor}`,
            }}
          />
          <div
            className="h-px"
            style={{
              width: `${Math.min(60 + loopCount * 8, 120)}px`,
              background: `linear-gradient(to left, transparent, ${content.titleColor.replace("0.8", "0.3").replace("0.85", "0.3").replace("0.9", "0.3").replace("0.95", "0.3")})`,
            }}
          />
        </div>

        {/* 本文 */}
        <div style={{ opacity: revealed ? 1 : 0, transition: "opacity 2s ease" }}>
          <p
            className="text-base leading-loose mb-6 max-w-lg mx-auto"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              color: `rgba(255,255,255,${0.12 + Math.min(loopCount - 5, 7) * 0.01})`,
            }}
          >
            {body1Lines.map((line, i) => (
              <span key={i}>{line}{i < body1Lines.length - 1 && <br />}</span>
            ))}
          </p>

          <p
            className="text-sm leading-loose mb-20 max-w-md mx-auto tracking-widest"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              color: content.titleColor.replace("0.8", "0.28").replace("0.85", "0.28").replace("0.9", "0.28").replace("0.95", "0.28"),
            }}
          >
            {body2Lines.map((line, i) => (
              <span key={i}>{line}{i < body2Lines.length - 1 && <br />}</span>
            ))}
          </p>

          {/* 2択ボタン */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <button
              onClick={handleEscape}
              className="border px-10 py-3 text-xs tracking-[0.5em] uppercase transition-all duration-700 cursor-pointer whitespace-nowrap bg-transparent"
              style={{
                borderColor: "rgba(255,255,255,0.08)",
                color: `rgba(255,255,255,${0.12 + Math.min(loopCount - 5, 5) * 0.01})`,
                fontFamily: "'Cormorant Garamond', serif",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = "rgba(255,255,255,0.18)";
                (e.currentTarget as HTMLElement).style.color = "rgba(255,255,255,0.25)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = "rgba(255,255,255,0.08)";
                (e.currentTarget as HTMLElement).style.color = `rgba(255,255,255,${0.12 + Math.min(loopCount - 5, 5) * 0.01})`;
              }}
            >
              {content.escapeBtn}
            </button>

            <button
              onClick={handleAccept}
              className="border px-10 py-3 text-xs tracking-[0.5em] uppercase transition-all duration-700 cursor-pointer whitespace-nowrap bg-transparent"
              style={{
                borderColor: content.titleColor.replace("0.8", "0.4").replace("0.85", "0.4").replace("0.9", "0.4").replace("0.95", "0.4"),
                color: content.titleColor.replace("0.8", "0.65").replace("0.85", "0.65").replace("0.9", "0.65").replace("0.95", "0.65"),
                fontFamily: "'Cormorant Garamond', serif",
                boxShadow: loopCount >= 8 ? `0 0 20px ${content.glowColor}` : "none",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = content.titleColor.replace("0.8", "0.7").replace("0.85", "0.7").replace("0.9", "0.7").replace("0.95", "0.7");
                (e.currentTarget as HTMLElement).style.color = content.titleColor.replace("0.8", "0.9").replace("0.85", "0.9").replace("0.9", "0.9").replace("0.95", "0.9");
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = content.titleColor.replace("0.8", "0.4").replace("0.85", "0.4").replace("0.9", "0.4").replace("0.95", "0.4");
                (e.currentTarget as HTMLElement).style.color = content.titleColor.replace("0.8", "0.65").replace("0.85", "0.65").replace("0.9", "0.65").replace("0.95", "0.65");
              }}
            >
              {content.acceptBtn}
            </button>
          </div>

          <p
            className="mt-12 text-xs tracking-[0.3em]"
            style={{
              color: `rgba(255,255,255,${0.04 + Math.min(loopCount - 5, 6) * 0.005})`,
              fontFamily: "'Cormorant Garamond', serif",
            }}
          >
            {content.footnote}
          </p>
        </div>

        {/* 下部ライン */}
        <div
          className="mx-auto mt-16"
          style={{
            width: "1px",
            height: "80px",
            background: `linear-gradient(to top, transparent, ${content.titleColor.replace("0.8", "0.2").replace("0.85", "0.2").replace("0.9", "0.2").replace("0.95", "0.2")})`,
            opacity: phase >= 2 ? 1 : 0,
            transition: "opacity 2s ease 1s",
          }}
        />
      </div>

      {/* 四隅の装飾（ループ回数に応じてサイズが変化） */}
      {revealed && (
        <>
          {[
            { top: "8px", left: "8px", borderTop: true, borderLeft: true },
            { top: "8px", right: "8px", borderTop: true, borderRight: true },
            { bottom: "8px", left: "8px", borderBottom: true, borderLeft: true },
            { bottom: "8px", right: "8px", borderBottom: true, borderRight: true },
          ].map((pos, i) => (
            <div
              key={i}
              className="absolute"
              style={{
                top: pos.top,
                left: (pos as { left?: string }).left,
                right: (pos as { right?: string }).right,
                bottom: pos.bottom,
                width: `${Math.min(24 + loopCount * 2, 48)}px`,
                height: `${Math.min(24 + loopCount * 2, 48)}px`,
                borderTop: pos.borderTop ? `1px solid ${content.titleColor.replace("0.8", "0.2").replace("0.85", "0.2").replace("0.9", "0.2").replace("0.95", "0.2")}` : "none",
                borderBottom: pos.borderBottom ? `1px solid ${content.titleColor.replace("0.8", "0.2").replace("0.85", "0.2").replace("0.9", "0.2").replace("0.95", "0.2")}` : "none",
                borderLeft: pos.borderLeft ? `1px solid ${content.titleColor.replace("0.8", "0.2").replace("0.85", "0.2").replace("0.9", "0.2").replace("0.95", "0.2")}` : "none",
                borderRight: pos.borderRight ? `1px solid ${content.titleColor.replace("0.8", "0.2").replace("0.85", "0.2").replace("0.9", "0.2").replace("0.95", "0.2")}` : "none",
              }}
            />
          ))}
        </>
      )}

      <style>{`
        @keyframes pulse {
          from { transform: scaleY(0.4); opacity: 0.3; }
          to   { transform: scaleY(1.0); opacity: 0.7; }
        }
        @keyframes abyssPulse {
          0%, 100% { opacity: 0.7; }
          50%      { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
